/**
 * Content Tracking Module
 * Tracks impressions and clicks on content blocks for analytics
 * 
 * Usage:
 * Add data attributes to any content block:
 * - data-content-tracking (required)
 * - data-content-name="Block Type - Description" (required)
 * 
 * Events sent to mParticle:
 * - "Content Impression" - when block is 30% visible
 * - "Content Click" - when any CTA in block is clicked
 */

class ContentTracker {
  constructor(element, contentName, pagePath) {
    this.element = element;
    this.contentName = contentName;
    this.pagePath = pagePath;
    this.impressionFired = false;
    this.clickFired = false;
    this.observer = null;
  }

  init() {
    this.initImpressionTracking();
    this.initClickTracking();
  }

  initImpressionTracking() {
    // Intersection Observer for 30% visibility threshold
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.3 && !this.impressionFired) {
            this.sendImpressionEvent();
            this.impressionFired = true;
            // Disconnect after firing to prevent duplicate events
            this.observer.disconnect();
          }
        });
      },
      { 
        threshold: 0.3,
        rootMargin: '0px'
      }
    );

    this.observer.observe(this.element);
  }

  initClickTracking() {
    // Find all CTAs (buttons, links) within the element
    const ctas = this.element.querySelectorAll('a[href], button[type="submit"], button:not([type])');
    
    ctas.forEach((cta) => {
      cta.addEventListener('click', () => {
        this.sendClickEvent();
      }, { once: true }); // Use once option to prevent duplicates
    });
  }

  sendImpressionEvent() {
    if (!this.impressionFired) {
      this.sendToMParticle('Impression', {
        content_name: this.contentName
      });
      this.impressionFired = true;
    }
  }

  sendClickEvent() {
    if (!this.clickFired) {
      this.sendToMParticle('Click', {
        content_name: this.contentName
      });
      this.clickFired = true;
    }
  }

  sendToMParticle(eventType, attributes) {
    // Use publishMParticleEvent (same as DY Active Campaigns)
    if (typeof publishMParticleEvent === 'function') {
      try {
        publishMParticleEvent({
          eventName: `Content ${eventType}`,
          eventType: 'Other',
          payload: attributes
        });
      } catch (error) {
        console.error('[Content Tracking] Error publishing event:', error);
      }
    } else {
      console.warn('[Content Tracking] publishMParticleEvent not available');
    }
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// Track initialized blocks to prevent duplicates
const initializedBlocks = new WeakSet();

// Initialize all tracked content blocks on page load
function initContentTracking(event) {
  // If this is a section:load event, only init blocks within that section
  const container = event?.target || document;
  const trackedBlocks = container.querySelectorAll('[data-content-tracking]');
  const pagePath = window.location.pathname;
  
  trackedBlocks.forEach((block) => {
    // Skip if already initialized
    if (initializedBlocks.has(block)) return;
    
    const contentName = block.dataset.contentName;
    
    if (contentName && contentName.trim() !== '') {
      const tracker = new ContentTracker(block, contentName, pagePath);
      tracker.init();
      initializedBlocks.add(block);
    } else {
      console.warn('[Content Tracking] Block missing content name:', block);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initContentTracking);
} else {
  initContentTracking();
}

// Re-initialize on Shopify section render (for theme editor)
document.addEventListener('shopify:section:load', initContentTracking);
